import React, { useEffect, useMemo, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  FormControl,
  MenuItem,
  Paper,
  Select,
  Slider,
  Stack,
  Switch,
  Typography,
  useTheme,
} from "@mui/material";
import { alpha } from "@mui/material/styles";
import RouteIcon from "@mui/icons-material/Route";
import BoltIcon from "@mui/icons-material/Bolt";
import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome";
import InsightsIcon from "@mui/icons-material/Insights";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";
import SaveIcon from "@mui/icons-material/Save";
import PowerSettingsNewIcon from "@mui/icons-material/PowerSettingsNew";

import {
  AnimatedModal,
  ModalContent,
  ModalFooter,
  ModalHeader,
} from "../modals/AnimatedModal";
import { dispatchSnackBarMessage } from "../../utils/snackbar";
import {
  getSmartRoutingConfig,
  getSmartRoutingPresets,
  previewSmartRouting,
  updateSmartRoutingConfig,
} from "../../api/smartRouting";

/* ────── data ────── */

const PRESET_META = [
  {
    key: "conservative",
    label: "Conservador",
    description: "rotas curtas e rápidas",
    icon: BoltIcon,
    palette: "info",
  },
  {
    key: "balanced",
    label: "Balanceado",
    description: "recomendado",
    icon: AutoAwesomeIcon,
    palette: "primary",
    recommended: true,
  },
  {
    key: "aggressive",
    label: "Agressivo",
    description: "lotação máxima",
    icon: InsightsIcon,
    palette: "secondary",
  },
];

const SECTIONS = [
  {
    title: "Limites de rota",
    description: "Quantos pedidos cabem numa rota e até onde ela pode ir",
    params: [
      "max_orders_per_route",
      "min_orders_to_form_route",
      "max_route_distance_km",
      "max_route_duration_minutes",
    ],
  },
  {
    title: "Direcionamento",
    description: "Como o algoritmo decide o que está 'no mesmo caminho'",
    params: [
      "max_bearing_spread_degrees",
      "max_detour_ratio",
      "avg_urban_speed_kmh",
    ],
  },
  {
    title: "Filtros",
    description: "Quais pedidos entram no agrupamento",
    params: ["max_order_age_minutes"],
  },
  {
    title: "Despacho",
    description: "O que acontece quando uma rota fica pronta",
    params: ["max_wait_time_minutes", "auto_dispatch", "prioritize"],
  },
];

const HINTS = {
  max_orders_per_route: "Máximo absoluto de pedidos numa única rota.",
  min_orders_to_form_route:
    "Abaixo desse número, os pedidos ficam aguardando companheiros de rota.",
  max_route_distance_km:
    "Soma das distâncias do trajeto (origem → entregas → origem).",
  max_route_duration_minutes:
    "Estimativa baseada na velocidade média urbana configurada abaixo.",
  max_bearing_spread_degrees:
    "Quanto menor, mais reta é a direção. Quanto maior, agrupa pedidos em direções mais distintas.",
  max_detour_ratio:
    "Resgata pares 'de passagem' (mesma rua/sentido) mesmo com bearings diferentes. 1.0 = sem desvio, 1.25 = aceita 25% extra.",
  max_wait_time_minutes:
    "Após esse tempo, o pedido sozinho aparece com alerta para despacho individual.",
  max_order_age_minutes:
    "Pedidos embalados há mais que esse tempo são ignorados (provavelmente abandonados). Padrão 360 = 6 horas.",
  avg_urban_speed_kmh:
    "Estimativa de velocidade média na sua cidade (não considera trânsito real).",
  auto_dispatch:
    "Quando uma rota válida é formada, atribui um motorista automaticamente.",
  prioritize:
    "Como o algoritmo pondera duração vs distância ao escolher a melhor rota.",
};

const PRIORITIZE_LABELS = {
  speed: "Velocidade",
  cost: "Custo",
  balanced: "Balanceado",
};

/* ────── main ────── */

export default function SmartRoutingConfigModal({ show, closeModal }) {
  const theme = useTheme();

  const [config, setConfig] = useState(null);
  const [presets, setPresets] = useState(null);
  const [labels, setLabels] = useState({});
  const [limits, setLimits] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [previewing, setPreviewing] = useState(false);
  const [previewResult, setPreviewResult] = useState(null);
  const [activePreset, setActivePreset] = useState(null);
  const [errorMessage, setErrorMessage] = useState("");

  useEffect(() => {
    if (!show) return;
    const load = async () => {
      setLoading(true);
      setPreviewResult(null);
      setActivePreset(null);
      setErrorMessage("");
      try {
        const [cfgRes, presetRes] = await Promise.all([
          getSmartRoutingConfig(),
          getSmartRoutingPresets(),
        ]);
        setConfig(cfgRes.data);
        setPresets(presetRes.data.presets);
        setLabels(presetRes.data.param_labels);
        setLimits(presetRes.data.param_limits);
      } catch (e) {
        const msg =
          e.response?.status === 402
            ? "Seu plano não inclui Roteirização Inteligente"
            : "Erro ao carregar configurações";
        setErrorMessage(msg);
        dispatchSnackBarMessage(`smart-routing-load-${Date.now()}`, msg, "error");
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [show]);

  const updateField = (key, value) => {
    setConfig((c) => ({ ...c, [key]: value }));
    setActivePreset(null);
  };

  const applyPreset = (presetKey) => {
    if (!presets?.[presetKey]) return;
    setConfig((c) => ({
      ...c,
      ...presets[presetKey],
      enabled: c.enabled,
    }));
    setActivePreset(presetKey);
    dispatchSnackBarMessage(
      `smart-routing-preset-${Date.now()}`,
      `Perfil "${PRESET_META.find((p) => p.key === presetKey)?.label}" aplicado`,
      "info"
    );
  };

  const onSave = async () => {
    if (!config) return;
    setSaving(true);
    try {
      const res = await updateSmartRoutingConfig(config);
      setConfig(res.data);
      dispatchSnackBarMessage(
        `smart-routing-save-${Date.now()}`,
        "Configuração de roteirização salva",
        "success"
      );
      closeModal();
    } catch (e) {
      dispatchSnackBarMessage(
        `smart-routing-save-${Date.now()}`,
        e.response?.data?.error || "Erro ao salvar configuração",
        "error"
      );
    } finally {
      setSaving(false);
    }
  };

  const onPreview = async () => {
    setPreviewing(true);
    setPreviewResult(null);
    try {
      const res = await previewSmartRouting(config);
      setPreviewResult(res.data);
    } catch (e) {
      dispatchSnackBarMessage(
        `smart-routing-preview-${Date.now()}`,
        e.response?.data?.error || "Erro ao pré-visualizar",
        "error"
      );
    } finally {
      setPreviewing(false);
    }
  };

  return (
    <AnimatedModal
      isOpen={show}
      onClose={closeModal}
      width="720px"
      maxWidth="95%"
    >
      <ModalHeader
        title="Roteirização Inteligente"
        icon={RouteIcon}
        onClose={closeModal}
      />

      <ModalContent>
        <Box sx={{ px: 3, pt: 2, pb: 3 }}>
          <AnimatePresence>
            {errorMessage && (
              <motion.div
                initial={{ opacity: 0, y: -10 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -10 }}
              >
                <Alert
                  severity="error"
                  onClose={() => setErrorMessage("")}
                  sx={{ mb: 2.5, borderRadius: 2 }}
                >
                  {errorMessage}
                </Alert>
              </motion.div>
            )}
          </AnimatePresence>

          {loading || !config ? (
            <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
              <CircularProgress />
            </Box>
          ) : (
            <>
              <EnabledCard
                enabled={config.enabled}
                onChange={(v) => updateField("enabled", v)}
                theme={theme}
              />

              <SectionHeader
                title="Perfis pré-definidos"
                description="Aplique uma configuração base e ajuste depois"
                theme={theme}
                sx={{ mt: 3 }}
              />
              <Stack
                direction={{ xs: "column", sm: "row" }}
                spacing={1.5}
                sx={{ mt: 1.5 }}
              >
                {PRESET_META.map((meta) => (
                  <PresetCard
                    key={meta.key}
                    meta={meta}
                    values={presets?.[meta.key]}
                    isSelected={activePreset === meta.key}
                    onClick={() => applyPreset(meta.key)}
                    theme={theme}
                  />
                ))}
              </Stack>

              {SECTIONS.map((section) => (
                <Box key={section.title} sx={{ mt: 3 }}>
                  <SectionHeader
                    title={section.title}
                    description={section.description}
                    theme={theme}
                  />
                  <Paper
                    elevation={0}
                    sx={{
                      mt: 1.5,
                      borderRadius: 2,
                      border: `1px solid ${alpha(theme.palette.divider, 0.4)}`,
                      overflow: "hidden",
                    }}
                  >
                    {section.params.map((key, idx) => (
                      <ParamRow
                        key={key}
                        paramKey={key}
                        label={labels[key] || key}
                        hint={HINTS[key]}
                        value={config[key]}
                        onChange={(v) => updateField(key, v)}
                        limits={limits[key]}
                        isLast={idx === section.params.length - 1}
                        theme={theme}
                      />
                    ))}
                  </Paper>
                </Box>
              ))}

              {previewResult && (
                <PreviewSection
                  result={previewResult}
                  theme={theme}
                  sx={{ mt: 3 }}
                />
              )}
            </>
          )}
        </Box>
      </ModalContent>

      {!loading && config && (
        <ModalFooter>
          <Stack
            direction="row"
            spacing={1.5}
            alignItems="center"
            sx={{ width: "100%", justifyContent: "space-between" }}
          >
            <Button
              onClick={onPreview}
              disabled={previewing || !config.enabled}
              variant="text"
              size="small"
              startIcon={
                previewing ? (
                  <CircularProgress size={14} />
                ) : (
                  <PlayArrowIcon fontSize="small" />
                )
              }
              sx={{ textTransform: "none" }}
            >
              Pré-visualizar com pedidos atuais
            </Button>
            <Stack direction="row" spacing={1}>
              <Button
                onClick={closeModal}
                variant="text"
                size="small"
                sx={{
                  textTransform: "none",
                  color: theme.palette.text.secondary,
                }}
              >
                Cancelar
              </Button>
              <Button
                onClick={onSave}
                disabled={saving}
                variant="contained"
                size="small"
                disableElevation
                startIcon={
                  saving ? (
                    <CircularProgress size={14} sx={{ color: "inherit" }} />
                  ) : (
                    <SaveIcon fontSize="small" />
                  )
                }
                sx={{ textTransform: "none", borderRadius: 1.5 }}
              >
                Salvar
              </Button>
            </Stack>
          </Stack>
        </ModalFooter>
      )}
    </AnimatedModal>
  );
}

/* ────── subcomponents ────── */

function SectionHeader({ title, description, theme, sx }) {
  return (
    <Box sx={sx}>
      <Typography
        variant="subtitle2"
        fontWeight={700}
        sx={{ color: theme.palette.text.primary, lineHeight: 1.2 }}
      >
        {title}
      </Typography>
      {description && (
        <Typography variant="caption" color="text.secondary">
          {description}
        </Typography>
      )}
    </Box>
  );
}

function EnabledCard({ enabled, onChange, theme }) {
  const accent = enabled
    ? theme.palette.success.main
    : theme.palette.grey[500];
  return (
    <Paper
      elevation={0}
      sx={{
        p: 2,
        borderRadius: 2,
        border: `2px solid ${
          enabled
            ? alpha(theme.palette.success.main, 0.4)
            : alpha(theme.palette.divider, 0.6)
        }`,
        background: enabled
          ? `linear-gradient(135deg, ${alpha(
              theme.palette.success.light,
              0.06
            )} 0%, ${alpha(theme.palette.success.main, 0.04)} 100%)`
          : theme.palette.background.paper,
        transition: "all 0.2s ease",
      }}
    >
      <Stack direction="row" spacing={1.5} alignItems="center">
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            bgcolor: alpha(accent, 0.12),
            color: accent,
            borderRadius: 1.5,
            width: 40,
            height: 40,
            flexShrink: 0,
            transition: "all 0.2s ease",
          }}
        >
          <PowerSettingsNewIcon fontSize="small" />
        </Box>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography variant="subtitle2" fontWeight={600}>
            Roteirização ativa
          </Typography>
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ display: "block", lineHeight: 1.5 }}
          >
            Quando um pedido é marcado como{" "}
            <Box
              component="span"
              sx={{
                fontFamily: "monospace",
                px: 0.5,
                borderRadius: 0.5,
                bgcolor: alpha(theme.palette.text.primary, 0.06),
              }}
            >
              EMBALADO
            </Box>
            , o algoritmo procura companheiros e sugere rotas no painel.
          </Typography>
        </Box>
        <Switch
          checked={!!enabled}
          onChange={(e) => onChange(e.target.checked)}
          color="success"
        />
      </Stack>
    </Paper>
  );
}

function PresetCard({ meta, values, isSelected, onClick, theme }) {
  const Icon = meta.icon;
  const accent =
    theme.palette[meta.palette]?.main || theme.palette.primary.main;

  return (
    <Paper
      elevation={isSelected ? 3 : 0}
      onClick={onClick}
      sx={{
        p: 1.75,
        cursor: "pointer",
        borderRadius: 2,
        flex: 1,
        position: "relative",
        border: `2px solid ${
          isSelected ? accent : alpha(theme.palette.divider, 0.4)
        }`,
        backgroundColor: isSelected
          ? alpha(accent, 0.05)
          : theme.palette.background.paper,
        transition: "all 0.18s ease",
        "&:hover": {
          borderColor: isSelected ? accent : alpha(accent, 0.4),
          transform: "translateY(-1px)",
        },
      }}
    >
      {meta.recommended && (
        <Chip
          label="Recomendado"
          color="primary"
          size="small"
          sx={{
            position: "absolute",
            top: 8,
            right: 8,
            height: 18,
            fontSize: "0.62rem",
            fontWeight: 600,
          }}
        />
      )}
      <Stack direction="row" spacing={1.5} alignItems="center">
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            bgcolor: isSelected
              ? alpha(accent, 0.12)
              : alpha(theme.palette.grey[500], 0.1),
            color: isSelected ? accent : theme.palette.grey[500],
            borderRadius: 1.5,
            p: 0.75,
            width: 34,
            height: 34,
            flexShrink: 0,
            transition: "all 0.18s ease",
          }}
        >
          <Icon fontSize="small" />
        </Box>
        <Box sx={{ minWidth: 0 }}>
          <Typography variant="subtitle2" fontWeight={600} lineHeight={1.2}>
            {meta.label}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {meta.description}
          </Typography>
        </Box>
      </Stack>
      {values && (
        <Box
          sx={{
            mt: 1.5,
            pt: 1.25,
            borderTop: `1px solid ${alpha(theme.palette.divider, 0.4)}`,
            display: "grid",
            gridTemplateColumns: "1fr 1fr",
            gap: 0.5,
            color: theme.palette.text.secondary,
            fontSize: "0.7rem",
            fontVariantNumeric: "tabular-nums",
          }}
        >
          <span>≤ {values.max_orders_per_route} pedidos</span>
          <span>≤ {values.max_route_distance_km} km</span>
          <span>{values.max_bearing_spread_degrees}° leque</span>
          <span>{values.max_route_duration_minutes} min</span>
        </Box>
      )}
    </Paper>
  );
}

function ParamRow({
  paramKey,
  label,
  hint,
  value,
  onChange,
  limits,
  isLast,
  theme,
}) {
  const isBoolean = typeof value === "boolean";
  const isSelect = paramKey === "prioritize";
  const isFloat = limits?.max != null && limits.max <= 5;
  const step = isFloat ? 0.05 : 1;
  const displayValue = isFloat ? Number(value).toFixed(2) : value;

  return (
    <Box
      sx={{
        px: 2.5,
        py: 1.75,
        borderBottom: isLast
          ? "none"
          : `1px solid ${alpha(theme.palette.divider, 0.4)}`,
        transition: "background 0.15s ease",
        "&:hover": {
          bgcolor: alpha(theme.palette.action.hover, 0.4),
        },
      }}
    >
      <Stack
        direction={{ xs: "column", sm: "row" }}
        spacing={{ xs: 1, sm: 2.5 }}
        alignItems={{ xs: "stretch", sm: "center" }}
      >
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography variant="body2" fontWeight={500}>
            {label}
          </Typography>
          {hint && (
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ display: "block", mt: 0.25, lineHeight: 1.5 }}
            >
              {hint}
            </Typography>
          )}
        </Box>

        {isBoolean ? (
          <Switch
            checked={!!value}
            onChange={(e) => onChange(e.target.checked)}
            color="primary"
          />
        ) : isSelect ? (
          <FormControl size="small" sx={{ minWidth: 180 }}>
            <Select
              value={value || "balanced"}
              onChange={(e) => onChange(e.target.value)}
              sx={{ fontSize: 13 }}
            >
              {Object.entries(PRIORITIZE_LABELS).map(([k, v]) => (
                <MenuItem key={k} value={k}>
                  {v}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        ) : (
          <Stack
            direction="row"
            spacing={2}
            alignItems="center"
            sx={{ minWidth: { sm: 240 } }}
          >
            <Slider
              value={value}
              min={limits?.min ?? 0}
              max={limits?.max ?? 100}
              step={step}
              onChange={(_, v) => onChange(v)}
              size="small"
              sx={{ flex: 1 }}
            />
            <Box
              sx={{
                minWidth: 56,
                textAlign: "right",
                px: 1,
                py: 0.4,
                borderRadius: 1,
                bgcolor: alpha(theme.palette.primary.main, 0.08),
                color: theme.palette.primary.main,
                fontWeight: 600,
                fontVariantNumeric: "tabular-nums",
                fontFamily: "monospace",
                fontSize: 13,
              }}
            >
              {displayValue}
            </Box>
          </Stack>
        )}
      </Stack>
    </Box>
  );
}

function PreviewSection({ result, theme, sx }) {
  const routes = result?.routes || [];
  const solo = result?.solo_pending || [];
  const empty = routes.length === 0 && solo.length === 0;

  return (
    <Box sx={sx}>
      <SectionHeader
        title="Pré-visualização"
        description={`${routes.length} ${
          routes.length === 1 ? "rota" : "rotas"
        } · ${solo.length} ${solo.length === 1 ? "avulso" : "avulsos"}`}
        theme={theme}
      />
      <Box sx={{ mt: 1.5 }}>
        {empty ? (
          <Alert severity="info" variant="outlined" sx={{ borderRadius: 2 }}>
            Nenhum pedido embalado no momento. Embale alguns pedidos ou ajuste
            o status que dispara o agrupamento.
          </Alert>
        ) : (
          <Stack spacing={1}>
            {routes.map((r, i) => (
              <Paper
                key={r.cluster_id}
                elevation={0}
                sx={{
                  p: 1.5,
                  borderRadius: 2,
                  border: `1px solid ${alpha(theme.palette.divider, 0.4)}`,
                  borderLeft: `3px solid ${theme.palette.primary.main}`,
                }}
              >
                <Stack
                  direction="row"
                  alignItems="baseline"
                  justifyContent="space-between"
                >
                  <Typography variant="subtitle2" fontWeight={600}>
                    Rota {String.fromCharCode(65 + i)}
                  </Typography>
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{
                      fontFamily: "monospace",
                      fontVariantNumeric: "tabular-nums",
                    }}
                  >
                    {r.total_distance_km.toFixed(1)} km ·{" "}
                    {Math.round(r.total_duration_minutes)} min ·{" "}
                    {Math.round(r.bearing_spread)}°
                  </Typography>
                </Stack>
                <Typography variant="caption" color="text.secondary">
                  {r.orders.length}{" "}
                  {r.orders.length === 1 ? "entrega" : "entregas"}
                </Typography>
              </Paper>
            ))}
            {solo.map((o) => (
              <Paper
                key={o.order_id}
                elevation={0}
                sx={{
                  p: 1.5,
                  borderRadius: 2,
                  border: `1px solid ${alpha(theme.palette.divider, 0.4)}`,
                  borderLeft: `3px solid ${
                    o.urgent
                      ? theme.palette.error.main
                      : theme.palette.info.main
                  }`,
                }}
              >
                <Stack
                  direction="row"
                  alignItems="baseline"
                  justifyContent="space-between"
                >
                  <Typography
                    variant="caption"
                    color={o.urgent ? "error.main" : "info.main"}
                    sx={{
                      letterSpacing: "0.08em",
                      fontWeight: 700,
                      textTransform: "uppercase",
                    }}
                  >
                    {o.urgent ? "Urgente" : "Aguardando"}
                  </Typography>
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{ fontFamily: "monospace" }}
                  >
                    {o.waiting_minutes?.toFixed(1)} min
                  </Typography>
                </Stack>
                <Typography variant="body2" sx={{ mt: 0.25 }}>
                  #{o.order_index}
                  {o.customer_name && ` · ${o.customer_name}`}
                </Typography>
              </Paper>
            ))}
          </Stack>
        )}
      </Box>
    </Box>
  );
}
